import { useCallback, useMemo, useState } from 'react'
import { Row, Table } from '@atbay/cyber-components'
import { AssetsModule } from '@core/modules/digital-footprint/assets'
import { useDrawer } from '@/core/context/DrawerContext'
import { Analytics } from '@/core/services/mixpanel'
import { getHighestSeverity } from '@routes/digital-footprint/routes/assets/components/assetsTable/utils'
import { getTableRowContext } from '@/ui/utils/tableUtils'
import {
    TAllIntegrations,
    TIntegration,
} from '@/core/modules/integrations/integrations'
import TrashIcon from '@assets/images/trash-icon.svg?react'
import RetryIcon from '@assets/images/retry-icon.svg?react'
import { ASM_DIALOGS_MAP, DialogService } from '@/core/services/dialog'
import { IntegrationsModule } from '@/core/modules/integrations'
import { IntegrationsStore } from '@/core/store/Integrations/integrations.store'

export type TIntegrationsTableRow = {
    asset?: TAsset
    integration: TIntegration
}

export const useIntegrationsTable = (
    integrations: TIntegration[],
    type: TAllIntegrations
) => {
    const { data: allAssets, loading: assetsLoading } = AssetsModule.useStore()
    const { isScansLoading } = IntegrationsStore.useStore()
    const isAssetsLoading = assetsLoading || isScansLoading
    const { setDrawerState, drawerState } = useDrawer()
    const [openMenuId, setOpenMenuId] = useState<string | undefined>(undefined)

    const closeMenu = useCallback(() => setOpenMenuId(undefined), [])

    const toggleMenu = useCallback((rowKey: string) => {
        setOpenMenuId((prev) => (prev === rowKey ? undefined : rowKey))
    }, [])

    const rows = useMemo<TIntegrationsTableRow[]>(() => {
        if (type === 'crowdstrike') {
            const integration = integrations.find((i) => i.type === type)
            if (!integration) return []
            const integrationAssets = allAssets.filter(
                (asset) => asset.asset_type === 'integration'
            )
            if (integrationAssets.length === 0) {
                return [{ integration }]
            }
            return integrationAssets.map((asset) => ({ asset, integration }))
        }

        const cloudAppAssets = allAssets.filter(
            (asset) => asset.asset_type === 'cloud_app'
        )

        return integrations.map((integration) => {
            const tenantIdentifier =
                integration.external_identifier ?? integration.external_name
            const asset = tenantIdentifier
                ? cloudAppAssets.find((a) => a.hostname === tenantIdentifier)
                : undefined
            return { integration, asset }
        })
    }, [allAssets, integrations, type])

    const getMenuItems = useCallback(
        (integration: TIntegration) => {
            const removeItem = {
                id: 'remove-account',
                value: 'Remove Account',
                action: () => {
                    DialogService.showDialog(
                        ASM_DIALOGS_MAP.DELETE_INTEGRATION,
                        {
                            providerId: integration.id,
                            integrationId: integration.integration_id,
                            type: integration.type,
                        }
                    )
                    closeMenu()
                },
                leftIcon: <TrashIcon height={16} width={16} />,
                dataTestId: 'integrations-table-remove-account-button',
            }

            if (integration.status !== 'broken') return [removeItem]

            const reconnectItem = {
                id: 'reconnect-integration',
                value: 'Reconnect',
                action: () => {
                    if (type === 'crowdstrike') {
                        setDrawerState((prev) => ({
                            ...prev,
                            isDrawerOpen: true,
                            drawerContentType: 'integration',
                            contentProps: { integration, onToggle: () => {} },
                        }))
                    } else {
                        IntegrationsModule.handleIntegrationLogin({
                            providerId: integration.id,
                            type: integration.type,
                            origin: 'integrations-page',
                            action: 'reconnect',
                        })
                    }
                    closeMenu()
                },
                leftIcon: <RetryIcon height={16} width={16} />,
                dataTestId: 'integrations-table-reconnect-integration-button',
            }
            return [reconnectItem, removeItem]
        },
        [type, setDrawerState, closeMenu]
    )

    const handleRowClick = useCallback(
        (
            row: Row<TIntegrationsTableRow>,
            table: Table<TIntegrationsTableRow>
        ) => {
            const { asset } = row.original
            if (!asset) return

            const { items } = getTableRowContext(row, table)
            const assets = items
                .map((item) => item.asset)
                .filter((item): item is TAsset => Boolean(item))
            const index = assets.indexOf(asset)

            setDrawerState((prev) => ({
                ...prev,
                isDrawerOpen: true,
                drawerContentType: 'assetDetails',
                contentProps: { assets, index },
            }))

            Analytics.event('SEM::user_clicks_at_asset', {
                origin: 'integrations_table',
                assetId: asset.id,
                assetType: asset.asset_type,
                severity: getHighestSeverity(asset.issues),
            })
        },
        [setDrawerState]
    )

    const activeAssetId = drawerState.isDrawerOpen
        ? drawerState.contentProps?.assets?.[drawerState.contentProps?.index]
              ?.id
        : undefined

    return {
        rows,
        handleRowClick,
        activeAssetId,
        openMenuId,
        toggleMenu,
        closeMenu,
        getMenuItems,
        isAssetsLoading,
    }
}
